import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export const AuthModal = ({ open, onClose, initialTab = "login" }) => {
  const { login, register } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState(initialTab);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isLogin = tab === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = isLogin ? await login(email, password) : await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(isLogin ? t("toast.welcomeBack") : t("toast.accountCreated"));
      onClose();
      setPassword("");
    } else {
      setError(res.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-md border-white/10 bg-[#12141F] p-0 overflow-hidden [&>button]:hidden"
        data-testid="auth-modal"
      >
        <div className="relative p-7">
          <DialogTitle className="sr-only">{isLogin ? "Log in to VidGrab" : "Create your VidGrab account"}</DialogTitle>
          <DialogDescription className="sr-only">
            {isLogin ? "Enter your email and password to log in." : "Enter your name, email and password to sign up."}
          </DialogDescription>          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-slate-500 hover:text-white transition-colors"
            data-testid="auth-close-button"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-600 to-cyan-500 grid place-items-center vg-glow">
              <Download size={18} className="text-white" />
            </div>
            <span className="text-xl font-head font-bold">VidGrab</span>
          </div>

          <div className="flex gap-1 p-1 rounded-xl bg-black/30 border border-white/5 mb-6" data-testid="auth-tab-switch">
            {["login", "register"].map((tb) => (
              <button
                key={tb}
                onClick={() => {
                  setTab(tb);
                  setError("");
                }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === tb ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white" : "text-slate-400 hover:text-white"
                }`}
                data-testid={`auth-switch-${tb}`}
              >
                {tb === "login" ? t("auth.login") : t("auth.signup")}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="relative"
                >
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    placeholder={t("auth.fullname")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={!isLogin}
                    className="pl-9 bg-black/30 border-white/10 h-11 text-white"
                    data-testid="register-name-input"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                type="email"
                placeholder={t("auth.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-9 bg-black/30 border-white/10 h-11 text-white"
                data-testid={isLogin ? "login-email-input" : "register-email-input"}
              />
            </div>

            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                type="password"
                placeholder={t("auth.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-9 bg-black/30 border-white/10 h-11 text-white"
                data-testid={isLogin ? "login-password-input" : "register-password-input"}
              />
            </div>

            {error && (
              <p className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 font-semibold text-white flex items-center justify-center gap-2 hover:opacity-95 transition-opacity disabled:opacity-60"
              data-testid={isLogin ? "login-submit-button" : "register-submit-button"}
            >
              {loading && <Loader2 size={18} className="vg-spin" />}
              {isLogin ? t("auth.login") : t("auth.create")}
            </button>
          </form>

          <p className="text-xs text-slate-500 text-center mt-5">
            {isLogin ? t("auth.newHere") : t("auth.have")}
            <button
              onClick={() => {
                setTab(isLogin ? "register" : "login");
                setError("");
              }}
              className="text-violet-400 hover:text-violet-300"
            >
              {isLogin ? t("auth.createOne") : t("auth.loginLink")}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
